/**
 * Mesure d'audience et marketing — Google Tag Manager, qui porte Google Analytics 4, le
 * pixel Meta et le traceur Brevo — chargée SEULEMENT après l'accord du visiteur.
 *
 * POURQUOI. Mesuré en ligne le 23/10/2026 :
 *   - index.html chargeait GTM, une balise gtag et le pixel Meta pour TOUS les visiteurs,
 *     2 s après l'affichage — ceux qui cliquaient « Refuser » compris. Aucun refus par
 *     défaut n'était posé, et la mise à jour du bandeau partait avant que `gtag` existe.
 *   - GA4 recevait 4 « page vues » par page, 6 sur un article de blog (gtag codé en dur,
 *     gtag réinjecté par le blog, deux balises Google dans le conteneur GTM).
 * Désormais : refus par défaut (Consent Mode v2), GTM chargé au premier accord et pas
 * avant, et le conteneur GTM pour seule source de « page vue ».
 *
 * ICI ET PAS DANS index.html. Les 327 pages pré-rendues ne gardent que les scripts du
 * gabarit index.html du moment (scripts/retirer-injections-tiers.mjs) : une logique
 * ajoutée à index.html n'y figurerait qu'après un nouveau pré-rendu. Le code de
 * l'application, lui, est chargé par toutes les pages, pré-rendues comprises.
 */
const ID_GTM = 'GTM-MHBG9K8F';

export interface ChoixMesure {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

// gtag pousse l'objet `arguments` lui-même : c'est ce que GTM attend, pas un tableau.
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer ??= []).push(arguments);
}

let initialise = false;
let gtmCharge = false;

const etat = (accord: boolean) => (accord ? 'granted' : 'denied');

/** Refus par défaut, posé avant tout chargement de balise. */
function initialiser() {
  if (initialise) return;
  initialise = true;
  window.gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });
}

function chargerGTM(differe: boolean) {
  if (gtmCharge) return;
  gtmCharge = true;
  const charger = () => {
    (window.dataLayer ??= []).push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${ID_GTM}`;
    document.head.appendChild(script);
  };
  if (!differe) return charger();
  // Accord déjà connu à l'ouverture de la page : GTM reste différé de 2 s après
  // « load », comme avant, pour ne pas alourdir le premier affichage.
  if (document.readyState === 'complete') setTimeout(charger, 2000);
  else window.addEventListener('load', () => setTimeout(charger, 2000), { once: true });
}

/**
 * Applique le choix du visiteur : met à jour le consentement et charge GTM au premier
 * accord (statistiques ou marketing). `aLOuverture` : choix déjà enregistré, relu au
 * chargement de la page.
 *
 * ponytail: un retrait du consentement coupe les cookies Google aussitôt, mais GTM déjà
 * chargé (pixel Meta, Brevo) le reste jusqu'au prochain chargement de page ; recharger
 * la page ferait perdre une saisie en cours.
 */
export function appliquerConsentement(choix: ChoixMesure, aLOuverture = false) {
  initialiser();
  gtag('consent', 'update', {
    analytics_storage: etat(choix.analytics),
    ad_storage: etat(choix.marketing),
    ad_user_data: etat(choix.marketing),
    ad_personalization: etat(choix.marketing),
    functionality_storage: etat(choix.functional),
    personalization_storage: etat(choix.functional),
  });
  if (choix.analytics || choix.marketing) chargerGTM(aLOuverture);
}
